/**
 * 报告复核 · 详情与复核动作。
 *
 * 这一屏的**唯一职责是把「这份报告是怎么来的」讲清楚**，因此三处诚实性信息必须显式呈现：
 *
 *   1. `generatedBy`：如实标注为**算法组装**（`sk-engine+rules` → 由三康螺旋引擎与规则组装）。
 *      本页任何位置都不允许出现「AI 生成」「大模型生成」——
 *      报告里的数字由 SK-Engine 算出、行文由规则模板按口径拼装，没有生成式模型参与；
 *   2. `calibrated`：为 false 时显示醒目的「未校准」告示，并附上服务端给出的标定说明。
 *      传导矩阵用的还是专家先验值，把它藏起来等于让专家为一条未标定的推演背书；
 *   3. 缺失字段：一律显示「未提供」。**绝不用 0 或 `—` 冒充** ——
 *      报告正文里字段缺失意味着「这一项没有依据」，写成 0 会被读成一个真实测得的零值。
 *
 * 复核区只在当前员工权限含 `report:review` 时出现（权限来自 `/api/admin/me`，不硬编码角色）。
 * 提交失败时（403 范围外 / 409 已复核等）**照实显示服务端原文**，不把失败渲染成成功。
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi, ApiError } from '../lib/api.ts';
import type { ReportDetail } from '../lib/api.ts';
import { useLoad } from '../lib/useLoad.ts';
import { Badge, Card, PageHead, PermissionBlock } from '../components/ui.tsx';
import { ErrorBox, LoadingBox, errorText } from '../components/StateBox.tsx';
import {
  MISSING_TEXT,
  calibratedNotice,
  formatConfidence,
  formatDateTime,
  generatedByLabel,
  plainText,
  reportField,
  reviewDecisionLabel,
  reviewStatusLabel,
  reviewStatusTone,
} from '../lib/format.ts';

/* ------------------------------------------------------------------ *
 * 结构化正文字段的呈现
 * ------------------------------------------------------------------ */

/**
 * 结构化正文的实际形状随报告类型而变（analysis / alert / plan 三套），
 * 这里只做「忠实呈现」，不做业务解释：能识别的字段用中文标签，识别不了的字段
 * 用原始键名显示 —— **宁可露出原始键，也不假装那一项不存在**。
 *
 * `null` 显式包含在联合类型里：JSON 里 null 是常见取值（例如尚未评估的置信度），
 * 类型上排除它只会把「如实呈现 null」挤成一次类型断言。
 */
type LooseValue = string | number | boolean | null | LooseValue[] | { [key: string]: LooseValue };

interface Entry {
  key: string;
  label: string;
  value: LooseValue;
}

/** 英文键 → 中文标签。只覆盖报告正文里反复出现的键，其余原样显示。 */
const FIELD_LABELS: Record<string, string> = {
  kind: '结构类型',
  question: '本报告回答的问题',
  sla: '生成时效口径',
  meta: '元信息',
  modelTag: '引擎与模型标识',
  generatedBy: '产出方式标识',
  confidence: '置信度',
  dataCut: '数据截止',
  calibrated: '引擎标定',
  notice: '标定说明',
  summary: '摘要',
  dataCompleteness: '数据完整度',
  missing: '缺失项',
  missingNarrative: '缺失说明',
  reportCount: '报告总数',
  parsedCount: '已解析份数',
  pendingCount: '解析中份数',
  failedCount: '解析失败份数',
  latestReportAt: '最近报告时间',
  measuredAt: '测量时间',
  axes: '三康轴评分',
  composite: '综合指数',
  formula: '计算公式',
  formulaNote: '公式说明',
  xai: '评分变化归因',
  items: '明细',
  positiveSharePct: '正向占比',
  negativeSharePct: '负向占比',
  note: '说明',
  health: '健康档案',
  bioAge: '生物年龄',
  realAge: '实际年龄',
  organAge: '器官年龄',
  dataSource: '数据来源',
  spiral: '螺旋状态',
  scale: '评估规模',
  threshold: '阈值',
  trajectory: '轨迹',
  transmission: '轴间传导',
  couplings: '传导通道',
  inbound: '被传导',
  outbound: '向外传导',
  narrative: '结论叙述',
  counterfactual: '反事实推演',
  scenario: '情景',
  months: '推演月数',
  horizon: '时间窗',
  warning: '预警窗',
  organ: '器官变化窗',
  accuracy: '精度',
  accuracyNote: '精度说明',
  openAlerts: '未解除预警',
  alertSummary: '预警汇总',
  byLevel: '按等级',
  total: '合计',
  maxConfidence: '最高置信度',
  earliestFoundAt: '最早发现时间',
  honestNotes: '诚实边界',
  baseline: '推演基线',
  projectedComposite: '基线推演综合指数',
  baselineSource: '基线来源',
  leverage: '杠杆测算',
  intensity: '干预强度',
  method: '方法',
  rows: '测算明细',
  plan: '干预动作',
  recovery: '恢复窗口',
  withIntervention: '是否含干预',
  recoveryMonth: '恢复月',
  windowNote: '窗口说明',
  finalComposite: '窗口末综合指数',
  review: '复评安排',
  nodes: '复评节点',
  cadence: '复评节奏',
  resourceSharePct: '资源占比',
  expectedGain: '预期收益',
  rationale: '总述',
  boundary: '边界',
  source: '原始数据取值',
  delta: '变化量',
  conclusion: '归因结论',
  value: '取值',
  grade: '等级',
  gradeText: '等级说明',
  key: '键',
  name: '名称',
  label: '项目',
  text: '内容',
  desc: '说明',
  action: '建议动作',
  status: '状态',
  level: '等级',
  foundAt: '发现时间',
  at: '时间',
  reason: '理由',
  axis: '轴',
  signedValue: '带符号贡献',
  direction: '方向',
  sharePct: '占比',
  color: '颜色',
  min: '最小值',
  minMonth: '最小值所在月',
  firstBelowMonth: '首次跌破月',
  belowAtEnd: '窗口末是否仍低于阈值',
  start: '起始值',
  final: '窗口末值',
  startComposite: '起始综合指数',
  input: '输入分值',
  shock: '冲击',
  shockNote: '冲击说明',
};

function labelOf(key: string): string {
  return FIELD_LABELS[key] ?? key;
}

function isLooseObject(value: LooseValue): value is { [key: string]: LooseValue } {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isLooseArray(value: LooseValue): value is LooseValue[] {
  return Array.isArray(value);
}

function isPrimitive(value: LooseValue): value is string | number | boolean {
  return typeof value !== 'object' || value === null;
}

/**
 * 标量 → 文案。null 与空串一律「未提供」，**不补 0**。
 *
 * 字符串走 `plainText`：服务端在推演情景、诚实边界这些说明性文案里带了 Markdown 强调标记
 * （`**不作为绝对预测**`），裸星号直接显示会被读成排版故障；这与成员详情页处理职责说明是同一做法。
 */
function primitiveText(value: LooseValue): string {
  if (value === null || value === undefined || value === '') return MISSING_TEXT;
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (typeof value === 'string') return plainText(value);
  return String(value);
}

/** 数组里全是标量时压成一行；含对象时交给递归渲染。 */
function scalarArrayText(items: LooseValue[]): string {
  return items.length === 0 ? MISSING_TEXT : items.map((item) => primitiveText(item)).join('、');
}

/**
 * 从对象里读一个布尔字段，并**区分「没有这个字段」与「值为 false」**。
 * `undefined` 表示未下发（按「未提供」呈现），`false` 是确认未标定（必须告警）。
 */
function booleanField(value: LooseValue | null, key: string): boolean | undefined {
  if (value === null || !isLooseObject(value)) return undefined;
  const raw = value[key];
  return typeof raw === 'boolean' ? raw : undefined;
}

function entriesOf(value: LooseValue): Entry[] {
  if (isLooseObject(value)) {
    return Object.entries(value).map(([key, nested]) => ({ key, label: labelOf(key), value: nested }));
  }
  return [];
}

/**
 * 递归渲染一个结构化值。
 *
 * 为什么递归：报告正文里 `axes`、`plan`、`couplings` 这些字段本身是对象数组，
 * 若在界面里逐类硬编码字段名，报告结构一变就会出现「少显示了一项，但没人发现」。
 * 递归呈现的代价是键名可能以英文露出，换取的是**服务端给了什么就能看到什么**。
 */
function JsonValue({ value, depth }: { value: LooseValue; depth: number }) {
  if (isPrimitive(value)) return <div className="kv-v text">{primitiveText(value)}</div>;

  if (isLooseArray(value)) {
    const scalarOnly = value.every((item) => isPrimitive(item));
    if (scalarOnly) return <div className="kv-v text">{scalarArrayText(value)}</div>;
    return (
      <div className="stack">
        {value.map((item, index) => (
          <div className="divider" key={index}>
            <div className="muted text-tiny">第 {index + 1} 项</div>
            <JsonValue value={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  // 深度兜底：正文嵌套层数有限（实测不超过 4 层）。再深就用原始 JSON 兜底，
  // 既不无限递归，也不悄悄吞掉服务端给的数据。
  if (depth >= 6) {
    let dumped: string;
    try {
      dumped = JSON.stringify(value);
    } catch {
      dumped = MISSING_TEXT;
    }
    return <pre className="duty">{dumped}</pre>;
  }

  const entries = entriesOf(value);
  if (entries.length === 0) return <div className="kv-v text">{MISSING_TEXT}</div>;
  return (
    <div className="stack">
      {entries.map((entry) => (
        <div key={entry.key}>
          <div className="kv-l">
            {entry.label}
            <span className="muted text-tiny mono-num"> {entry.key}</span>
          </div>
          <JsonValue value={entry.value} depth={depth + 1} />
        </div>
      ))}
    </div>
  );
}

/** 取正文里一个可识别字段；取不到、为 null 或空数组都算「未提供」。 */
function pick(payload: Record<string, LooseValue>, key: string): LooseValue | null {
  const value = payload[key];
  if (value === undefined || value === null) return null;
  if (isLooseArray(value) && value.length === 0) return null;
  return value;
}

function Missing({ what }: { what: string }) {
  return (
    <div className="kv-v text">
      {MISSING_TEXT}
      <span className="muted text-tiny">（接口未下发「{what}」字段，界面不猜）</span>
    </div>
  );
}

/** 正文分节：能识别的字段单独成节，其余键值对原样列出。 */
function PayloadSections({ payload }: { payload: Record<string, LooseValue> }) {
  const allKeys = Object.keys(payload);
  if (allKeys.length === 0) {
    return (
      <div className="state-box">
        <div className="st-ico">◍</div>
        <div className="st-title">本报告的结构化正文未提供</div>
        <div>服务端返回的 payload 为空对象；界面不推断正文内容。</div>
      </div>
    );
  }

  const meta = pick(payload, 'meta');
  const axes = pick(payload, 'axes');
  const composite = pick(payload, 'composite');
  const xai = pick(payload, 'xai');
  const completeness = pick(payload, 'dataCompleteness');
  const health = pick(payload, 'health');
  const scale = pick(payload, 'scale');
  const spiral = pick(payload, 'spiral');
  const alertSummary = pick(payload, 'alertSummary');
  const counterfactual = pick(payload, 'counterfactual');
  const transmission = pick(payload, 'transmission');
  const horizon = pick(payload, 'horizon');
  const honestNotes = pick(payload, 'honestNotes');
  const baseline = pick(payload, 'baseline');
  const leverage = pick(payload, 'leverage');
  const plan = pick(payload, 'plan');
  const recovery = pick(payload, 'recovery');
  const reviewPlan = pick(payload, 'review');

  const used = new Set([
    'kind',
    'question',
    'sla',
    'meta',
    'axes',
    'composite',
    'xai',
    'dataCompleteness',
    'health',
    'scale',
    'spiral',
    'alertSummary',
    'openAlerts',
    'counterfactual',
    'transmission',
    'horizon',
    'honestNotes',
    'baseline',
    'leverage',
    'plan',
    'recovery',
    'review',
    'source',
  ]);
  const otherKeys = allKeys.filter((key) => !used.has(key));
  const bodyCalibrated = booleanField(meta, 'calibrated');
  const bodyCalibration = calibratedNotice(bodyCalibrated);
  // 先收窄到局部常量再进 JSX：条件表达式里直接用 `meta` 会丢掉类型收窄
  const modelTagText = isLooseObject(meta) && meta.modelTag !== undefined ? primitiveText(meta.modelTag) : null;

  return (
    <div className="stack">
      <div className="grid2">
        <div>
          <div className="kv-l">本报告回答的问题</div>
          {pick(payload, 'question') === null ? (
            <Missing what="question" />
          ) : (
            <JsonValue value={payload.question as LooseValue} depth={0} />
          )}
        </div>
        <div>
          <div className="kv-l">生成时效口径</div>
          {pick(payload, 'sla') === null ? <Missing what="sla" /> : <JsonValue value={payload.sla as LooseValue} depth={0} />}
        </div>
      </div>

      <div className="divider" />

      <div className="kv">
        <div>
          <div className="kv-l">结构类型 kind</div>
          {pick(payload, 'kind') === null ? <Missing what="kind" /> : <JsonValue value={payload.kind as LooseValue} depth={0} />}
        </div>
        <div>
          <div className="kv-l">正文里的标定状态</div>
          <div className={`kv-v text${bodyCalibration.warn ? ' hl-w' : ''}`}>{bodyCalibration.text}</div>
        </div>
        <div>
          <div className="kv-l">引擎与模型标识</div>
          {modelTagText === null ? (
            <Missing what="meta.modelTag" />
          ) : (
            <div className="kv-v text">{modelTagText}</div>
          )}
        </div>
      </div>

      {axes !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            三康轴评分
            <small>报告落库时的取值，逐轴列出</small>
          </div>
          <JsonValue value={axes} depth={0} />
        </>
      )}

      {composite !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            综合指数
            <small>由三轴分值按权重算出，公式随报告给出</small>
          </div>
          <JsonValue value={composite} depth={0} />
        </>
      )}

      {spiral !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            螺旋状态
          </div>
          <JsonValue value={spiral} depth={0} />
        </>
      )}

      {xai !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            评分变化归因（XAI）
            <small>带符号贡献，负向贡献不为正</small>
          </div>
          <JsonValue value={xai} depth={0} />
        </>
      )}

      {scale !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            评估规模
          </div>
          <JsonValue value={scale} depth={0} />
        </>
      )}

      {health !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            健康档案取值
            <small>生物年龄 / 器官年龄来自档案实测，不在报告里推算</small>
          </div>
          <JsonValue value={health} depth={0} />
        </>
      )}

      {completeness !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            数据完整度
            <small>缺失项如实列出，报告不填补这些项</small>
          </div>
          <JsonValue value={completeness} depth={0} />
        </>
      )}

      {alertSummary !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            预警汇总
          </div>
          <JsonValue value={alertSummary} depth={0} />
        </>
      )}

      {pick(payload, 'openAlerts') !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            未解除预警明细
          </div>
          <JsonValue value={payload.openAlerts as LooseValue} depth={0} />
        </>
      )}

      {counterfactual !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            反事实推演
            <small>传导矩阵标定状态随正文一并给出</small>
          </div>
          <JsonValue value={counterfactual} depth={0} />
        </>
      )}

      {transmission !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            轴间传导
          </div>
          <JsonValue value={transmission} depth={0} />
        </>
      )}

      {horizon !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            时间窗
          </div>
          <JsonValue value={horizon} depth={0} />
        </>
      )}

      {baseline !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            推演基线
            <small>实测综合指数与「不干预」推演基线逐一区分</small>
          </div>
          <JsonValue value={baseline} depth={0} />
        </>
      )}

      {leverage !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            杠杆测算
          </div>
          <JsonValue value={leverage} depth={0} />
        </>
      )}

      {plan !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            干预动作
          </div>
          <JsonValue value={plan} depth={0} />
        </>
      )}

      {recovery !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            恢复窗口
          </div>
          <JsonValue value={recovery} depth={0} />
        </>
      )}

      {reviewPlan !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            复评安排
          </div>
          <JsonValue value={reviewPlan} depth={0} />
        </>
      )}

      {honestNotes !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            报告自带的诚实边界
          </div>
          <JsonValue value={honestNotes} depth={0} />
        </>
      )}

      {pick(payload, 'source') !== null && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            生成本报告时实际取用的原始数据
            <small>用于逐值回溯，不含任何推算值</small>
          </div>
          <JsonValue value={payload.source as LooseValue} depth={0} />
        </>
      )}

      {otherKeys.length > 0 && (
        <>
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            其余字段
            <small>界面未单独识别，按原始键名照实列出</small>
          </div>
          <JsonValue
            value={Object.fromEntries(otherKeys.map((key) => [key, payload[key] as LooseValue]))}
            depth={0}
          />
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * 复核区
 * ------------------------------------------------------------------ */

function ReviewPanel({ report, onDone }: { report: ReportDetail; onDone: (text: string) => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (decision: 'approved' | 'revise'): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      const result = await adminApi.reviewReport(report.id, decision, note.trim());
      const decisionText = decision === 'approved' ? '复核通过' : '已标记为需修订';
      onDone(
        `${decisionText}：${result.reportNo} · 复核人 ${result.reviewer?.name ?? MISSING_TEXT}` +
          `${result.note ? ` · 备注「${result.note}」` : ' · 未填写备注'}`,
      );
    } catch (err) {
      // 403（缺 report:review 权限点 / 成员在可见范围外）与其它服务端拒绝都照实显示原文，
      // 不能把被拒绝的复核显示成成功 —— 成员侧会看到这条审计，写错了无法悄悄收回。
      setError(
        err instanceof ApiError
          ? `复核未落库（${err.status}）：${err.message}`
          : '复核请求失败，请确认后端服务可用',
      );
      setBusy(false);
    }
  };

  return (
    <div className="dispatch-panel">
      <div className="dp-t">提交复核 · {report.title}</div>
      <div className="dp-s">
        复核结论会连同您的姓名 + 角色与工号写入审计链，成员在自己的「数据调用记录」里能看到是谁、
        基于什么结论复核了这份报告。备注最多 500 字，会与结论一并留痕。
      </div>
      <div className="field">
        <label className="field-label" htmlFor="review-note">
          复核备注（可选，例如：已核对原始检测报告，第 3 项归因需人工确认）
        </label>
        <input
          id="review-note"
          className="field-input"
          placeholder="填写判读依据或需要修订的具体内容"
          value={note}
          maxLength={500}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      {error && <div className="field-error">{error}</div>}
      <div className="row">
        <button type="button" className="btn primary sm" disabled={busy} onClick={() => void submit('approved')}>
          {busy ? '正在提交…' : '通过'}
        </button>
        <button type="button" className="btn sm" disabled={busy} onClick={() => void submit('revise')}>
          需修订
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * 页面
 * ------------------------------------------------------------------ */

export function ReportDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const reportId = params.id ?? '';
  const detail = useLoad((signal) => adminApi.report(reportId, signal));
  /** 权限清单来自 /api/admin/me：复核区是否出现由此决定，不按角色名硬编码。 */
  const me = useLoad((signal) => adminApi.me(signal));
  const [toast, setToast] = useState<string | null>(null);

  if (detail.loading) return <LoadingBox text="正在加载报告详情…" />;

  if (detail.error) {
    const err = detail.error;
    // 403：可能是角色缺 report:read，也可能是该成员不在守护关系内 —— 两种都照实说。
    if (err instanceof ApiError && err.status === 403) {
      return (
        <div>
          <PageHead title="无权查看该报告" sub="403 · 权限拦截" />
          <Card>
            <PermissionBlock
              title="该报告不在您的可见范围内"
              desc={`服务端返回：${err.message}。报告读取需要 report:read 权限点，且该成员必须在您的守护关系内；两者缺一都会被拒绝 —— 界面不会把这次拦截显示成空报告。`}
            />
            <div className="sp12" />
            <button type="button" className="btn" onClick={() => navigate('/reports')}>
              返回报告列表
            </button>
          </Card>
        </div>
      );
    }
    if (err instanceof ApiError && err.status === 404) {
      return (
        <div>
          <PageHead title="报告不存在" sub="404" />
          <ErrorBox message={`服务端返回：${err.message}。该报告编号在库中不存在，可能已被移除或编号有误。`} />
          <div className="sp12" />
          <button type="button" className="btn" onClick={() => navigate('/reports')}>
            返回报告列表
          </button>
        </div>
      );
    }
    return <ErrorBox message={errorText(err)} onRetry={detail.reload} />;
  }

  if (!detail.data) return <ErrorBox message="报告详情为空" onRetry={detail.reload} />;

  const report = detail.data;
  const payload = (report.payload ?? {}) as Record<string, LooseValue>;
  const payloadKeys = Object.keys(payload);
  const honesty = report.honesty;
  const calibration = calibratedNotice(honesty?.calibrated);
  const missing = honesty?.missingData ?? [];
  const canReview = (me.data?.permissions ?? []).includes('report:review');

  const handleDone = (text: string): void => {
    setToast(text);
    detail.reload();
    window.setTimeout(() => setToast(null), 6000);
  };

  return (
    <div>
      <PageHead
        title="报告复核"
        sub={`报告编号 ${report.reportNo} · 类型 ${reportField(report.typeName)} · 成员 ${reportField(
          report.member?.name,
        )}（${reportField(report.member?.no)}）`}
        actions={
          <button type="button" className="btn sm" onClick={() => navigate('/reports')}>
            返回报告列表
          </button>
        }
      />

      {/* 诚实性信息集中呈现：产出方式、标定状态、缺失项一眼可见 */}
      <Card title="诚实边界" sub="产出方式 · 引擎标定 · 缺失项">
        <div className="kv">
          <div>
            <div className="kv-l">产出方式 generatedBy（如实标识：{reportField(report.generatedBy)}）</div>
            <div className="kv-v text">{generatedByLabel(report.generatedBy)}</div>
          </div>
          <div>
            <div className="kv-l">引擎标定 calibrated</div>
            <div className={`kv-v text${calibration.warn ? ' hl-w' : ''}`}>{calibration.text}</div>
          </div>
          <div>
            <div className="kv-l">置信度 confidence</div>
            <div className="kv-v text">
              {formatConfidence(report.confidence)}
              {report.confidence === null && (
                <span className="muted text-tiny">（本报告未给出置信度，界面不补 0）</span>
              )}
            </div>
          </div>
        </div>

        {honesty?.generatedByNote !== undefined && honesty.generatedByNote !== '' && (
          <div className="note-src">
            <b>产出方式说明（服务端原文）</b>
            <br />
            {plainText(honesty.generatedByNote)}
          </div>
        )}

        {calibration.warn && (
          <div className="alert-chip warn">
            <b>未校准</b>
            <div>
              {honesty?.calibratedNote !== undefined && honesty.calibratedNote !== ''
                ? plainText(honesty.calibratedNote)
                : '服务端未给出标定说明，界面不代为解释。'}
            </div>
          </div>
        )}

        <div className="divider" />

        <div className="kv-l">缺失项 missingData</div>
        {missing.length === 0 ? (
          <div className="kv-v text">无缺失项</div>
        ) : (
          <div className="perm-cells">
            {missing.map((item) => (
              <span className="badge warn" key={item}>
                {item}
              </span>
            ))}
          </div>
        )}
        <div className="note-src">
          <b>为什么把这三项单独提出来</b>
          <br />
          报告里凡是没有依据的字段，一律显示「{MISSING_TEXT}」而<b>不是 0</b> ——
          写成 0 会被读成「测出来就是零」。同样，本报告由算法组装（{reportField(report.generatedBy)}），
          不存在生成式模型编造数字的可能；这句话必须由界面说出来，不能靠口口相传。
        </div>
      </Card>

      <Card title="报告基本信息" sub="来自报告表，未做二次加工">
        <div className="kv">
          <div>
            <div className="kv-l">报告标题</div>
            <div className="kv-v text">{reportField(report.title)}</div>
          </div>
          <div>
            <div className="kv-l">成员</div>
            <div className="kv-v text">
              {reportField(report.member?.name)}（{reportField(report.member?.no)}）
            </div>
          </div>
          <div>
            <div className="kv-l">生成时间</div>
            <div className="kv-v">{formatDateTime(report.generatedAt)}</div>
          </div>
          <div>
            <div className="kv-l">数据截止</div>
            <div className="kv-v">{formatDateTime(report.dataCut)}</div>
          </div>
          <div>
            <div className="kv-l">引擎与模型标识</div>
            <div className="kv-v text">{reportField(report.modelTag)}</div>
          </div>
          <div>
            <div className="kv-l">报告状态</div>
            <div className="kv-v text">
              <Badge tone={reviewStatusTone(report.status)}>{reviewStatusLabel(report.status)}</Badge>
            </div>
          </div>
          <div>
            <div className="kv-l">复核结论</div>
            <div className="kv-v text">{reviewDecisionLabel(report.reviewStatus)}</div>
          </div>
          <div>
            <div className="kv-l">复核人 / 复核时间</div>
            <div className="kv-v text">
              {report.reviewer
                ? `${reportField(report.reviewer.name)} · ${formatDateTime(report.reviewer.at)}`
                : MISSING_TEXT}
            </div>
          </div>
        </div>
        <div className="kv">
          <div>
            <div className="kv-l">复核备注 review_note</div>
            <div className="kv-v text">
              {report.reviewer && report.reviewer.note !== '' ? report.reviewer.note : MISSING_TEXT}
            </div>
          </div>
          <div>
            <div className="kv-l">摘要</div>
            <div className="kv-v text">{reportField(report.summary)}</div>
          </div>
        </div>
      </Card>

      <Card title="结构化正文" sub={`payload · ${payloadKeys.length} 个顶层字段`}>
        {payloadKeys.length === 0 ? (
          <div className="state-box">
            <div className="st-ico">◍</div>
            <div className="st-title">正文未提供</div>
            <div>服务端未下发 payload，或该报告为生成失败；界面不推断正文内容。</div>
          </div>
        ) : (
          <PayloadSections payload={payload} />
        )}
      </Card>

      {canReview ? (
        <Card title="复核动作" sub="需要 report:review 权限点 · 结论写入审计链">
          <ReviewPanel report={report} onDone={handleDone} />
        </Card>
      ) : (
        <Card title="复核动作" sub="当前员工无权复核">
          <PermissionBlock
            title="当前角色没有「report:review」权限"
            desc={
              me.loading
                ? '正在向 /api/admin/me 校验权限点…'
                : me.error
                  ? `权限清单读取失败：${errorText(me.error)}。在读到权限之前，本页不显示复核按钮。`
                  : '复核要求 report:review 权限点，运营、财务、数据运营等角色都没有这个权限点，因此看不到复核区。这不是界面故障，是权限模型的正常结果。'
            }
          />
        </Card>
      )}

      <div className="note-src">
        <b>复核留痕</b>
        <br />
        提交复核会把「复核通过 / 需修订」、您的姓名 + 角色、工号与备注写进审计链（同一事务内落库），
        成员在自己的「数据调用记录」里可见。因此复核被服务端拒绝时，界面会原样显示服务端给的原因，
        不做乐观更新 —— 让专家以为已经复核成功、实际没有落库，是这一屏最不能犯的错。
      </div>

      {toast && <div className="toast ok">{toast}</div>}
    </div>
  );
}
